<template>
  <div class="space-y-4">
    <div>
      <label
        class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5"
        for="ccnumber"
      >
        Card Number
      </label>
      <!-- These inputs are mounted/secured by the Freedom to Design SDK. -->
      <!-- The IDs (ccnumber/ccexpiry/cccvv) must stay intact for the SDK. -->
      <input
        id="ccnumber"
        inputmode="numeric"
        placeholder="1234 5678 9012 3456"
        class="w-full p-3 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all"
      />
    </div>

    <div class="grid grid-cols-2 gap-4">
      <div>
        <label
          class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5"
          for="ccexpiry"
        >
          Expiry
        </label>
        <input
          id="ccexpiry"
          inputmode="numeric"
          placeholder="MM/YY"
          maxlength="5"
          class="w-full p-3 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-center transition-all"
        />
      </div>
      <div>
        <label
          class="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5"
          for="cccvv"
        >
          CVV
        </label>
        <input
          id="cccvv"
          inputmode="numeric"
          placeholder="•••"
          maxlength="4"
          class="w-full p-3 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-center transition-all"
        />
      </div>
    </div>

    <p v-if="errorMessage" class="mt-1 text-xs text-red-500">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
const FTD_SCRIPT_ID = "ftd";
const FTD_SCRIPT_SRC_PROD = "https://payment.ipospays.com/ftd/v1/freedomtodesign.js";
const FTD_SCRIPT_SRC_TEST = "https://payment.ipospays.tech/ftd/v1/freedomtodesign.js";

export default {
  name: "DejavooCardForm",
  props: {
    securityKey: {
      type: String,
      required: true
    },
    testMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      errorMessage: ""
    };
  },
  mounted() {
    if (!this.securityKey) {
      console.warn("[DejavooCardForm] securityKey missing");
      return;
    }
    this.loadSdk();

    // Keep the expiry as a clean MM/YY (digits only + single slash) so the
    // SDK reads a well-formed value.
    const expiry = document.getElementById("ccexpiry");
    if (expiry) {
      this._onExpiryInput = () => {
        expiry.value = this.normalizeExpiry(expiry.value);
      };
      expiry.addEventListener("input", this._onExpiryInput);
    }
  },
  beforeDestroy() {
    const expiry = document.getElementById("ccexpiry");
    if (expiry && this._onExpiryInput) {
      expiry.removeEventListener("input", this._onExpiryInput);
    }
    // Remove the SDK script so re-entering checkout re-initializes cleanly.
    const existing = document.getElementById(FTD_SCRIPT_ID);
    if (existing) {
      existing.remove();
    }
  },
  methods: {
    loadSdk() {
      const scriptSrc = this.testMode ? FTD_SCRIPT_SRC_TEST : FTD_SCRIPT_SRC_PROD;
      const existing = document.getElementById(FTD_SCRIPT_ID);
      // If a script with a different key or host is present, replace it.
      if (existing) {
        if (
          existing.getAttribute("security_key") === this.securityKey &&
          existing.src === scriptSrc
        ) {
          return;
        }
        existing.remove();
      }

      const script = document.createElement("script");
      script.id = FTD_SCRIPT_ID;
      script.src = scriptSrc;
      script.setAttribute("security_key", this.securityKey);
      script.defer = true;
      script.onerror = () => {
        this.errorMessage = "Unable to load the secure payment form.";
      };
      document.head.appendChild(script);
    },
    normalizeExpiry(v) {
      const d = v.replace(/\D/g, "").slice(0, 4); // keep up to MMYY
      return d.length >= 3 ? d.slice(0, 2) + "/" + d.slice(2) : d;
    },
    async createToken() {
      this.errorMessage = "";

      if (typeof window.postData !== "function") {
        this.errorMessage = "Payment form is not ready. Please try again.";
        return {
          payment_token_id: null,
          error: { message: this.errorMessage }
        };
      }

      const expiry = document.getElementById("ccexpiry");
      if (expiry) {
        // Sanitize right before the SDK reads it.
        expiry.value = this.normalizeExpiry(expiry.value);
      }

      try {
        const response = await window.postData();
        const tokenId = response && response.payment_token_id;
        if (!tokenId) {
          this.errorMessage =
            (response && response.message) || "Unable to process card";
          return {
            payment_token_id: null,
            error: { message: this.errorMessage }
          };
        }
        this.errorMessage = "";
        return { payment_token_id: tokenId, error: null };
      } catch (error) {
        this.errorMessage = error?.message || "Unable to process card";
        return {
          payment_token_id: null,
          error: { message: this.errorMessage }
        };
      }
    }
  }
};
</script>
